import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

/**
 * M25 L25.04 — Prediction explainability + human-in-the-loop action.
 *
 * Reads `GET /v1/ai/predictions/:id` (each prediction carries a human-readable
 * explanation and ranked key factors, BR-03) and lets a reviewer record an
 * action via `POST /v1/ai/predictions/:id/action` (override needs a reason).
 */
interface Prediction {
  readonly predictionId: string;
  readonly predictionType: string;
  readonly entityType: string;
  readonly entityId: string | null;
  /** The record it is about, named from its master (G18). */
  readonly subjectCode: string | null;
  readonly subjectName: string | null;
  readonly predictedValue: string | null;
  readonly predictedLabel: string | null;
  readonly confidenceScore: string;
  readonly riskLevel: string | null;
  readonly isLowConfidence: boolean;
  readonly explanation: Record<string, unknown> | null;
  readonly keyFactors: Record<string, unknown> | null;
  readonly modelVersionLabel: string | null;
  readonly status: string;
  readonly predictedAt: string;
}

const ACTIONS = ['ACCEPT', 'OVERRIDE', 'REJECT'];

function Field({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--color-bg-muted)' }}>
      <span style={{ width: 180, color: 'var(--color-text-muted)', fontSize: 13 }}>{label}</span>
      <span style={{ fontSize: 13 }}>{value ?? '—'}</span>
    </div>
  );
}

function renderJson(obj: Record<string, unknown> | null): ReactNode {
  if (!obj) return '—';
  const reason = typeof obj.reason === 'string' ? obj.reason : null;
  return (
    <div>
      {reason && <p style={{ margin: '0 0 6px' }}>{reason}</p>}
      <pre style={{ margin: 0, fontSize: 12, background: 'var(--color-bg-muted)', padding: 8, borderRadius: 4, overflowX: 'auto' }}>
        {JSON.stringify(obj, null, 2)}
      </pre>
    </div>
  );
}

export function PredictionExplainPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { predictionId } = useParams<{ predictionId: string }>();
  const [row, setRow] = useState<Prediction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [userAction, setUserAction] = useState('ACCEPT');
  const [overrideReason, setOverrideReason] = useState('');
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setRow(null);
    setError(null);
    try {
      const r = await apiRequest<{ data: Prediction }>(
        `/v1/ai/predictions/${encodeURIComponent(predictionId ?? '')}`, { token },
      );
      setRow(r.data);
    } catch (e) { setError((e as Error).message); }
  }, [token, predictionId]);

  useEffect(() => { void load(); }, [load]);

  const submitAction = async (e: FormEvent) => {
    e.preventDefault();
    setActionMsg(null);
    if (userAction === 'OVERRIDE' && !overrideReason.trim()) {
      setActionMsg(t('m25.explain.reasonRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await apiRequest(`/v1/ai/predictions/${encodeURIComponent(predictionId ?? '')}/action`, {
        method: 'POST',
        token,
        body: { userAction, overrideReason: overrideReason.trim() || undefined },
      });
      setActionMsg(t('m25.explain.actionRecorded'));
      await load();
    } catch (err) {
      setActionMsg((err as Error).message);
    } finally { setSubmitting(false); }
  };

  return (
    <main aria-label="prediction-explain">
      <Link to="/admin/ml/hitl-queue" data-testid="back-queue" style={{ fontSize: 13 }}>{t('m25.explain.back')}</Link>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!row ? (
        !error && <p>Loading…</p>
      ) : (
        <>
          <h1 style={{ fontSize: 20, margin: '12px 0 16px' }}>
            {t('m25.explain.title')}
            {row.isLowConfidence && (
              <span data-testid="low-confidence" style={{ marginLeft: 8, fontSize: 12, padding: '2px 8px', borderRadius: 10, background: 'var(--color-warning-bg)', color: 'var(--color-warning)' }}>
                {t('m25.explain.lowConfidence')}
              </span>
            )}
          </h1>

          <section aria-label="prediction-fields" data-testid={`prediction-${row.predictionId}`}>
            <Field label={t('m25.explain.fType')} value={row.predictionType} />
            <Field label={t('m25.explain.fEntity')} value={`${row.entityType}${row.subjectCode ? ` · ${row.subjectCode} — ${row.subjectName ?? ''}` : ''}`} />
            <Field label={t('m25.explain.fPredictedValue')} value={row.predictedLabel ?? row.predictedValue} />
            <Field label={t('m25.explain.fConfidence')} value={row.confidenceScore} />
            <Field label={t('m25.explain.fRiskLevel')} value={row.riskLevel} />
            <Field label={t('m25.explain.fModelVersion')} value={row.modelVersionLabel} />
            <Field label={t('m25.explain.fStatus')} value={row.status} />
            <Field label={t('m25.explain.fExplanation')} value={renderJson(row.explanation)} />
            <Field label={t('m25.explain.fKeyFactors')} value={renderJson(row.keyFactors)} />
          </section>

          <h2 style={{ fontSize: 15, margin: '20px 0 8px' }}>{t('m25.explain.recordDecision')}</h2>
          {actionMsg && <p role="status" style={{ color: actionMsg === t('m25.explain.actionRecorded') ? 'var(--color-success)' : 'var(--color-danger)' }}>{actionMsg}</p>}
          <form aria-label="action-form" onSubmit={submitAction} style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 420 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              {t('m25.explain.action')}
              <select aria-label="user-action" value={userAction} onChange={(e) => setUserAction(e.target.value)}
                      style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
                {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
            {userAction === 'OVERRIDE' && (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                {t('m25.explain.overrideReason')}
                <textarea aria-label="override-reason" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                          rows={2} style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </label>
            )}
            <button type="submit" data-testid="submit-action" disabled={submitting}
                    style={{ padding: '8px 16px', borderRadius: 4, border: '1px solid var(--color-brand-primary)', background: 'var(--color-brand-primary)', color: 'var(--color-text-inverse)', alignSelf: 'flex-start' }}>
              {submitting ? t('m25.explain.saving') : t('m25.explain.recordAction')}
            </button>
          </form>
        </>
      )}
    </main>
  );
}
